import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, CardBody, Button, Breadcrumb, BreadcrumbItem } from 'reactstrap';
import { Wifi, List, Target, Send, TrendingUp, Users, RefreshCw } from 'react-feather';
import CountUp from 'react-countup';
import ReactApexChart from 'react-apexcharts';
import api from '../../api/prospect';
import { useProspectAuth } from '../../Auth/ProspectAuthContext';
import cartoonImg from '../../assets/images/dashboard/cartoon.svg';

// ── Gradientes por métrica ────────────────────────────────────────────────────
const GRADIENTS = {
  connections: 'linear-gradient(26deg,#a26cf8,#cc8ffd)',
  lists:       'linear-gradient(26deg,#f48665,#fda23f)',
  campaigns:   'linear-gradient(26deg,#04b1e0,#6ddaf1)',
  sent:        'linear-gradient(26deg,#f9e473,#f3bd4e)',
  rate:        'linear-gradient(26deg,#29d9d5,#1aacdc)',
  leads:       'linear-gradient(26deg,#54ba4a,#16C7F9)',
};

const STATUS_COLORS = {
  draft:'#94a3b8', scheduled:'#7366ff', running:'#54ba4a',
  paused:'#FFAA05', completed:'#16C7F9', cancelled:'#f44336',
};
const STATUS_LABELS = {
  draft:'Rascunho', scheduled:'Agendada', running:'Em disparo',
  paused:'Pausada', completed:'Concluída', cancelled:'Cancelada',
};

// ── Sparkline options ─────────────────────────────────────────────────────────
function sparkOpts(color) {
  return {
    chart: { type: 'area', sparkline: { enabled: true }, toolbar: { show: false }, animations: { enabled: false } },
    stroke: { curve: 'smooth', width: 2 },
    fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.35, opacityTo: 0 } },
    colors: [color],
    tooltip: { enabled: false },
  };
}

// ── Dados derivados de sparkline por métrica (mock visual) ────────────────────
const SPARK = {
  connections: [3,4,4,5,5,6,5,7,6,8],
  lists:       [2,3,3,4,4,5,5,6,6,7],
  campaigns:   [1,2,2,3,3,4,4,5,5,6],
  sent:        [10,18,14,25,22,35,30,48,42,60],
  rate:        [5,7,6,9,8,11,10,13,12,15],
  leads:       [1,1,2,2,3,3,4,4,5,6],
};

// ── Widget card no padrão Cuba widget-1 ──────────────────────────────────────
function Widget({ label, value, gradient, colorClass, Icon, suffix, sparkKey }) {
  const sparkColor = colorClass === 'warning' ? '#ffaa05'
    : colorClass === 'success'  ? '#54ba4a'
    : colorClass === 'info'     ? '#16c7f9'
    : colorClass === 'secondary'? '#f73164'
    : colorClass === 'danger'   ? '#f44336'
    : '#7366ff';

  return (
    <Card className='widget-1 widget-with-chart h-100'>
      <CardBody style={{ display: 'flex', flexDirection: 'column' }}>
        <div className='widget-content'>
          <div className={`widget-round ${colorClass}`}>
            <div className='bg-round' style={{ background: gradient, overflow: 'hidden', width: 54, height: 54, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Icon color='#fff' strokeWidth={1.5} style={{ width: 24, height: 24, flexShrink: 0 }} />
            </div>
          </div>
          <div>
            <h4 className='mb-1 f-w-600'>
              <CountUp end={typeof value === 'number' ? value : parseFloat(value) || 0} duration={1.5} separator='.' decimals={suffix === '%' ? 1 : 0} />{suffix || ''}
            </h4>
            <span className='f-light f-12'>{label}</span>
          </div>
        </div>
        <div style={{ marginTop: 'auto' }}>
          <ReactApexChart
            options={sparkOpts(sparkColor)}
            series={[{ data: SPARK[sparkKey] }]}
            type='area' height={50} width='100%'
          />
        </div>
      </CardBody>
    </Card>
  );
}

// ── Componente principal ──────────────────────────────────────────────────────
export default function Dashboard() {
  const { user }             = useProspectAuth();
  const navigate             = useNavigate();
  const [data, setData]      = useState(null);
  const [loading, setLoading]= useState(true);
  const timer                = useRef(null);

  async function fetchData() {
    try {
      const { data: d } = await api.get('/dashboard');
      setData(d);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }

  useEffect(() => {
    fetchData();
    timer.current = setInterval(fetchData, 30000);
    return () => clearInterval(timer.current);
  }, []);

  const d         = data || {};
  const tenantName = user?.name || user?.email?.split('@')[0] || 'usuário';

  // Gráfico de barras: atividade dos últimos 7 dias
  const activityData = (d.activity_7d || []).map(r => ({
    name: r.day,
    Disparos:  r.sent    || 0,
    Respostas: r.replied || 0,
  }));

  // Donut por status
  const byStatus = (d.campaigns_by_status || []).map(s => ({
    name: STATUS_LABELS[s.status] || s.status,
    value: s.total,
    color: STATUS_COLORS[s.status] || '#94a3b8',
  }));

  const replyRate   = parseFloat(d.taxa_resposta) || 0;
  const donutSeries = [replyRate, Math.max(0, 100 - replyRate)];

  const barOpts = {
    chart: { type: 'bar', toolbar: { show: false }, fontFamily: 'Rubik, sans-serif' },
    plotOptions: { bar: { horizontal: false, columnWidth: '40%', borderRadius: 6 } },
    dataLabels: { enabled: false },
    colors: ['#7366ff', '#54ba4a'],
    xaxis: { categories: activityData.map(r => r.name), axisBorder: { show: false }, axisTicks: { show: false }, labels: { style: { fontSize: '11px' } } },
    yaxis: { labels: { style: { fontSize: '11px' } } },
    grid: { borderColor: '#f1f1f1', strokeDashArray: 4 },
    legend: { position: 'top', fontSize: '12px' },
    tooltip: { shared: true, intersect: false },
  };

  const donutOpts = {
    chart: { type: 'donut', fontFamily: 'Rubik, sans-serif' },
    colors: ['#7366ff', '#f0f0f0'],
    labels: ['Responderam', 'Não responderam'],
    plotOptions: {
      pie: {
        donut: {
          size: '70%',
          labels: {
            show: true,
            total: {
              show: true,
              label: 'Taxa',
              fontSize: '13px',
              fontWeight: 600,
              color: '#7366ff',
              formatter: () => `${replyRate.toFixed(1)}%`,
            },
          },
        },
      },
    },
    dataLabels: { enabled: false },
    legend: { position: 'bottom', fontSize: '12px' },
    stroke: { width: 0 },
  };

  return (
    <>
      {/* Título + Breadcrumb + Botão Atualizar */}
      <Container fluid>
        <div className='page-title'>
          <Row>
            <Col xs='6'>
              <h3 className='mb-0'>Dashboard</h3>
            </Col>
            <Col xs='6' className='d-flex justify-content-end align-items-center gap-3'>
              <Breadcrumb className='mb-0'>
                <BreadcrumbItem><span className='f-light'>Topo Prospect</span></BreadcrumbItem>
                <BreadcrumbItem active>Dashboard</BreadcrumbItem>
              </Breadcrumb>
              <Button color='light' size='sm' onClick={fetchData} disabled={loading} style={{ flexShrink: 0 }}>
                <RefreshCw size={13} className={`me-1 ${loading ? 'fa-spin' : ''}`} /> Atualizar
              </Button>
            </Col>
          </Row>
        </div>
      </Container>
      <Container fluid>

        {/* ── Banner de boas-vindas (profile-box) ──────────────────────────── */}
        <Card className='profile-box mb-4' style={{ overflow: 'visible' }}>
          <CardBody style={{ padding: '30px 30px 20px', position: 'relative', minHeight: 140 }}>
            <div className='greeting-user'>
              <div className='badge badge-light-primary mb-2' style={{ fontSize: 11, padding: '4px 10px', borderRadius: 20, background: 'rgba(255,255,255,0.2)', color: '#fff' }}>
                Topo Prospect
              </div>
              <h4 className='f-w-600 mb-1' style={{ color: '#fff', fontSize: 20 }}>
                Bem-vindo, {tenantName}!
              </h4>
              <p className='mb-0' style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13, maxWidth: 480 }}>
                Gerencie seus disparos, acompanhe respostas e qualifique leads pelo WhatsApp em um só lugar.
              </p>
              <div className='whatsnew-btn mt-3'>
                <Button
                  size='sm'
                  style={{ background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.4)', color: '#fff', borderRadius: 20, padding: '6px 18px' }}
                  onClick={() => navigate('/campanhas/nova')}
                >
                  Nova Campanha
                </Button>
              </div>
            </div>
            <div className='cartoon' style={{ bottom: 0 }}>
              <img src={cartoonImg} alt='illustration' style={{ width: 140 }} />
            </div>
          </CardBody>
        </Card>

        {/* ── 6 widgets de métrica ─────────────────────────────────────────── */}
        <Row className='mb-2 align-items-stretch'>
          <Col xl='2' md='4' sm='6' className='mb-4'>
            <Widget label='Conexões ativas' value={loading ? 0 : d.total_connections ?? 0}
              gradient={GRADIENTS.connections} colorClass='primary' Icon={Wifi} sparkKey='connections' />
          </Col>
          <Col xl='2' md='4' sm='6' className='mb-4'>
            <Widget label='Total de listas' value={loading ? 0 : d.total_lists ?? 0}
              gradient={GRADIENTS.lists} colorClass='warning' Icon={List} sparkKey='lists' />
          </Col>
          <Col xl='2' md='4' sm='6' className='mb-4'>
            <Widget label='Campanhas' value={loading ? 0 : d.total_campaigns ?? 0}
              gradient={GRADIENTS.campaigns} colorClass='info' Icon={Target} sparkKey='campaigns' />
          </Col>
          <Col xl='2' md='4' sm='6' className='mb-4'>
            <Widget label='Total de disparos' value={loading ? 0 : d.total_sent ?? 0}
              gradient={GRADIENTS.sent} colorClass='warning' Icon={Send} sparkKey='sent' />
          </Col>
          <Col xl='2' md='4' sm='6' className='mb-4'>
            <Widget label='Taxa de resposta' value={loading ? 0 : replyRate}
              gradient={GRADIENTS.rate} colorClass='info' Icon={TrendingUp} suffix='%' sparkKey='rate' />
          </Col>
          <Col xl='2' md='4' sm='6' className='mb-4'>
            <Widget label='Leads qualificados' value={loading ? 0 : d.leads_qualificados ?? 0}
              gradient={GRADIENTS.leads} colorClass='success' Icon={Users} sparkKey='leads' />
          </Col>
        </Row>

        {/* ── Gráficos ─────────────────────────────────────────────────────── */}
        <Row>
          <Col xl='8' className='mb-4'>
            <Card className='h-100'>
              <CardBody>
                <div className='d-flex align-items-center justify-content-between mb-4'>
                  <h5 className='mb-0 f-w-600'>Atividade dos últimos 7 dias</h5>
                </div>
                {activityData.length === 0 ? (
                  <div className='text-center py-5 text-muted'>
                    <Send size={36} className='mb-2' />
                    <p>Nenhum dado de atividade disponível</p>
                  </div>
                ) : (
                  <ReactApexChart
                    options={barOpts}
                    series={[
                      { name: 'Disparos',  data: activityData.map(r => r.Disparos)  },
                      { name: 'Respostas', data: activityData.map(r => r.Respostas) },
                    ]}
                    type='bar' height={280}
                  />
                )}
              </CardBody>
            </Card>
          </Col>

          <Col xl='4' className='mb-4'>
            <Card className='h-100'>
              <CardBody>
                <div className='d-flex align-items-center justify-content-between mb-4'>
                  <h5 className='mb-0 f-w-600'>Taxa de resposta</h5>
                </div>
                {replyRate === 0 && loading ? (
                  <div className='text-center py-5 text-muted'>
                    <TrendingUp size={36} className='mb-2' />
                    <p>Nenhuma campanha concluída</p>
                  </div>
                ) : (
                  <ReactApexChart
                    options={donutOpts}
                    series={donutSeries}
                    type='donut' height={280}
                  />
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* ── Campanhas por status (recharts pie) ──────────────────────────── */}
        {byStatus.length > 0 && (
          <Row>
            <Col xl='12' className='mb-4'>
              <Card>
                <CardBody>
                  <h5 className='mb-4 f-w-600'>Campanhas por status</h5>
                  <Row>
                    {byStatus.map((s, i) => (
                      <Col xl='2' md='4' sm='6' key={i} className='mb-3'>
                        <div className='d-flex align-items-center gap-2'>
                          <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: '50%', background: s.color, flexShrink: 0 }} />
                          <div>
                            <p className='f-12 mb-0 text-muted'>{s.name}</p>
                            <h6 className='mb-0 f-w-600' style={{ color: s.color }}>{s.value}</h6>
                          </div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}

        <p className='text-muted f-10 text-end mt-1'>Atualiza automaticamente a cada 30s</p>
      </Container>
    </>
  );
}
